import { AUTH_CONFIG } from '@/config/constants';
import type { CurrentUser } from '@/types';
import { getImpersonation, stopImpersonation } from './impersonation';

export interface DecodedJWT {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: any;
}

export const decodeJWT = (token: string): DecodedJWT | null => {
  try {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const storeAuthData = (token: string): void => {
  localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
  const decoded = decodeJWT(token);
  if (decoded) {
    const role =
      decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
      decoded.role ||
      'User';
    const email =
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
      decoded.email ||
      '';
    const name =
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
      decoded.unique_name ||
      decoded.name ||
      email;
    const id = decoded.sub || decoded.nameid || '1';

    localStorage.setItem(AUTH_CONFIG.ROLE_KEY, role);
    const user: CurrentUser = { id, email, fullName: name, role };
    localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
  }
};

export const clearAuthData = (): void => {
  stopImpersonation();
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  localStorage.removeItem(AUTH_CONFIG.ROLE_KEY);
};

export const getToken = (): string | null => {
  return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
};

export const isTokenExpired = (): boolean => {
  const token = getToken();
  if (!token) return true;
  const decoded = decodeJWT(token);
  if (!decoded?.exp) return true;
  return Math.floor(Date.now() / 1000) >= decoded.exp;
};

export const getCurrentUser = (): CurrentUser | null => {
  const userJson = localStorage.getItem(AUTH_CONFIG.USER_KEY);
  if (!userJson) return null;
  try {
    return JSON.parse(userJson) as CurrentUser;
  } catch {
    return null;
  }
};

/**
 * Get the current effective role. While impersonating (see utils/impersonation.ts)
 * this is the TARGET's role — every role-derived decision in the app (menus,
 * guards, permissions) must follow the impersonated profile, exactly like the
 * API does per request. The real role stays stored untouched and comes back
 * the moment impersonation stops.
 */
export const getCurrentRole = (): string | null => {
  const impersonation = getImpersonation();
  if (impersonation) return impersonation.role;
  return localStorage.getItem(AUTH_CONFIG.ROLE_KEY);
};
